import { Route } from "@angular/router";

import { environment } from "../environments/environment";

import { authGuard } from "./core/guards/auth.guard";
import { superAdminGuard } from "./core/guards/super-admin.guard";
import { ShellComponent } from "./core/layout/shell.component";
import { feeCalculatorRoutes } from "./features/fee-calculator/fee-calculator.routes";
import { rulesAuthoringRoutes } from "./features/rules-authoring/rules-authoring.routes";

export const appRoutes: Route[] = [
  ...(environment.production
    ? []
    : [
        {
          path: "__thread-parity",
          loadComponent: () =>
            import("./features/orders/thread-panel/dev-harness/thread-parity-harness.component").then(
              (m) => m.ThreadParityHarnessComponent,
            ),
        },
      ]),
  {
    path: "login",
    loadComponent: () =>
      import("./features/login/login.component").then((m) => m.LoginComponent),
  },
  {
    path: "",
    component: ShellComponent,
    canActivate: [authGuard],
    children: [
      { path: "", redirectTo: "dashboard", pathMatch: "full" },
      {
        path: "dashboard",
        loadComponent: () =>
          import("./features/dashboard/dashboard.component").then(
            (m) => m.DashboardComponent,
          ),
      },
      {
        path: "dashboard/board",
        loadComponent: () =>
          import("./features/dashboard/board/order-board.component").then(
            (m) => m.OrderBoardComponent,
          ),
        data: { title: "Order Board" },
      },
      {
        path: "dashboard/recordings",
        loadComponent: () =>
          import("./features/dashboard/recordings/recording-board.component").then(
            (m) => m.RecordingBoardComponent,
          ),
        data: { title: "Recordings" },
      },
      {
        path: "dashboard/recordings/reference-notes",
        loadComponent: () =>
          import("./features/recording-notes-admin/recording-notes-admin-page.component").then(
            (m) => m.RecordingNotesAdminPageComponent,
          ),
        data: { title: "Recording Notes" },
      },
      {
        path: "messages",
        loadComponent: () =>
          import("./features/messages/messages-page.component").then(
            (m) => m.MessagesPageComponent,
          ),
        data: { title: "Messages" },
      },
      // Deep link to one order's conversation (row selection is page state).
      {
        path: "messages/:orderId",
        loadComponent: () =>
          import("./features/messages/messages-page.component").then(
            (m) => m.MessagesPageComponent,
          ),
        data: { title: "Messages" },
      },
      {
        path: "dashboard/customer-waiting",
        loadComponent: () =>
          import("./features/customer-waiting/customer-waiting-page.component").then(
            (m) => m.CustomerWaitingPageComponent,
          ),
        data: { title: "Customer Waiting" },
      },
      // Must precede `orders/:id` — the param route would otherwise swallow "new".
      {
        path: "orders/new",
        loadComponent: () =>
          import("./features/orders/order-create/order-create.component").then(
            (m) => m.OrderCreateComponent,
          ),
        data: { title: "New Order" },
      },
      {
        path: "orders/:id",
        loadComponent: () =>
          import("./features/orders/order-detail.component").then(
            (m) => m.OrderDetailComponent,
          ),
      },
      {
        path: "fill-out",
        loadComponent: () =>
          import("./features/orders/fill-out/fill-out.component").then(
            (m) => m.FillOutComponent,
          ),
      },
      {
        path: "preview-and-print",
        loadComponent: () =>
          import("./features/orders/preview-and-print/preview-and-print.component").then(
            (m) => m.PreviewAndPrintComponent,
          ),
      },
      {
        path: "profile",
        loadComponent: () =>
          import("./features/profile/profile.component").then(
            (m) => m.ProfileComponent,
          ),
      },
      {
        path: "deedcore/drafting/:id",
        loadComponent: () =>
          import("./features/deedcore-drafting/deedcore-drafting-detail.component").then(
            (m) => m.DeedcoreDraftingDetailComponent,
          ),
        data: { title: "DeedCore — Draft Review" },
      },
      {
        path: "rules",
        loadComponent: () =>
          import("./features/rules-authoring/rules-authoring-shell.component").then(
            (m) => m.RulesAuthoringShellComponent,
          ),
        children: rulesAuthoringRoutes,
        canActivate: [authGuard, superAdminGuard],
        data: { title: "Rules" },
      },
      {
        path: "payments/record-check",
        loadComponent: () =>
          import("./features/payments/record-check-page/record-check-page.component").then(
            (m) => m.RecordCheckPageComponent,
          ),
        canActivate: [superAdminGuard],
        data: { title: "Record Check Payment" },
      },
      {
        path: "fee-calculator",
        children: feeCalculatorRoutes,
        canActivate: [authGuard],
        data: { title: "Fee Calculator" },
      },
      {
        path: "deed-recordings",
        children: [
          {
            path: "",
            loadComponent: () =>
              import("./features/deed-recordings/list/deed-recordings-list.component").then(
                (m) => m.DeedRecordingsListComponent,
              ),
            data: { title: "US Deed Recordings — Orders" },
          },
          {
            path: ":guid",
            loadComponent: () =>
              import("./features/deed-recordings/detail/deed-recordings-detail.component").then(
                (m) => m.DeedRecordingsDetailComponent,
              ),
            data: { title: "US Deed Recordings — Order Detail" },
          },
        ],
      },
    ],
  },
  { path: "**", redirectTo: "dashboard" },
];
